import * as React from 'react';
import { useEffect, useMemo } from 'react';

import { PlanType } from '@showbie-socrative/socrative-utils';
import { useAtomValue } from 'jotai';

import { AiQuotaPaywallEventAdapter } from '~analytics/adapters/AiQuotaPaywallEventAdapter';
import type { AiFeatureType } from '~analytics/adapters/AiQuotaPaywallEventAdapter';
import { AiQuotaUpgradedEventAdapter } from '~analytics/adapters/AiQuotaUpgradedEventAdapter';
import { translate } from '~l10n/client';
import { languageAtom } from '~shared/atoms/languageAtom';
import { SmallLinkButton } from '~shared/components/buttons';
import { useCurrentPlan } from '~teacher/components/PricingTable/hooks';

import { InlineMessage } from '../InlineMessage/InlineMessage';
import { QuotaBucket } from './types';

const hundredPercentQuotaMessages: Record<
  QuotaBucket,
  {
    title: string;
    messageWithDate: string;
    messageWithoutDate: string;
  }
> = {
  Import: {
    title: translate('Import limit reached.'),
    messageWithDate: translate(
      'Upgrade to keep importing this week. Limit resets {0}'
    ),
    messageWithoutDate: translate('Upgrade to continue importing.'),
  },
  Generate: {
    title: translate('Question generation limit reached.'),
    messageWithDate: translate(
      'Upgrade to keep creating this week. Limit resets {0}'
    ),
    messageWithoutDate: translate('Upgrade to continue generating.'),
  },
};

interface HundredPercentQuotaInlineMessageProps {
  limitResetDate: Date | null;
  bucket: QuotaBucket;
  upgradeHref: string;
  percentUsed?: number;
}

const BUCKET_TO_FEATURE_MAP: Record<QuotaBucket, AiFeatureType> = {
  Generate: 'generate',
  Import: 'copypaste',
};

export function HundredPercentQuotaInlineMessage({
  limitResetDate,
  bucket,
  upgradeHref,
  percentUsed = 100,
}: HundredPercentQuotaInlineMessageProps) {
  const locale = useAtomValue(languageAtom);
  const currentPlanQuery = useCurrentPlan();
  const currentPlan = currentPlanQuery.data ?? PlanType.Free;
  const featureType = BUCKET_TO_FEATURE_MAP[bucket];

  useEffect(() => {
    new AiQuotaPaywallEventAdapter({
      featureType,
      limitType: 'hard',
    }).sendAnalytics();
  }, [featureType]);

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        hour: 'numeric',
        minute: 'numeric',
      }),
    [locale]
  );

  const messages = hundredPercentQuotaMessages[bucket];
  const body = limitResetDate
    ? messages.messageWithDate.format(dateFormatter.format(limitResetDate))
    : messages.messageWithoutDate;

  const handleUpgradeClick = () => {
    new AiQuotaUpgradedEventAdapter({
      featureType,
      limitType: 'hard',
      percentUsed,
      currentPlan,
    }).sendAnalytics();
  };

  return (
    <InlineMessage appearance="orange">
      <div className="flex gap-3 justify-between">
        <div>
          <strong>{messages.title}</strong> {body}
        </div>
        <div className="flex flex-col justify-center">
          <SmallLinkButton
            href={upgradeHref}
            appearance="error"
            onClick={handleUpgradeClick}
          >
            {translate('Upgrade')}
          </SmallLinkButton>
        </div>
      </div>
    </InlineMessage>
  );
}
